import { useDeviceStorage } from '../adapters/ports.js';

// Local persistence for the Live tab's attended sale + recent sales, through
// the host's device-storage port. There is no backend for it: which sale a
// dealer is standing in is a fact about this device, and a second device
// signed in as the same dealer is legitimately somewhere else.
//
// V2 STORES IDS, for the reason followStorage.js records: v1 stored the sale's
// NAME, so a rename stood the dealer in a sale that no longer existed and a
// weekly name pointed at the wrong week.
//
//   { selectedSale: saleRefId, recentSales: [saleRefId], pending: { selectedSale, recentSales } }
//
// `pending` holds the v1 names until the sale index can turn them into ids, and
// keeps the ones it cannot — a name that names two sales is never guessed at.
// The v1 key is removed only once nothing is pending.
const ATTEND_KEY = 'aac.attend.v2';
const LEGACY_ATTEND_KEY = 'aac.attend.v1';

// Keep the recents row short — it's a quick-switch aid, not a history.
export const RECENTS_MAX = 6;

const EMPTY = { selectedSale: null, recentSales: [], pending: { selectedSale: null, recentSales: [] } };

const name = (value) => (typeof value === 'string' ? value : null);
const names = (value) => (Array.isArray(value) ? value.filter((each) => typeof each === 'string').slice(0, RECENTS_MAX) : []);

const pendingOf = (value) => ({
  selectedSale: name(value?.selectedSale),
  recentSales: names(value?.recentSales)
});

const nothingPending = (pending) => pending.selectedSale === null && pending.recentSales.length === 0;

// Never rejects, and never returns a half-shape: the port answers null for both
// a missing value and a malformed one, and the field-by-field read below turns
// anything else unexpected into the empty selection. The Live tab can then
// hydrate and auto-select a sale rather than failing on the boot path.
//
// A device still on v1 answers with nothing attended and the stored names
// pending, so the dealer is put back in their sale as soon as the index can say
// which one it is.
export async function getAttendance() {
  const storage = useDeviceStorage();
  const parsed = await storage.getJson(ATTEND_KEY);
  if (!parsed) {
    const legacy = await storage.getJson(LEGACY_ATTEND_KEY);
    if (!legacy) return { ...EMPTY, pending: { selectedSale: null, recentSales: [] } };
    return { selectedSale: null, recentSales: [], pending: pendingOf(legacy) };
  }
  return {
    selectedSale: name(parsed.selectedSale),
    recentSales: names(parsed.recentSales),
    pending: pendingOf(parsed.pending)
  };
}

export async function setAttendance(value) {
  const storage = useDeviceStorage();
  const stillPending = pendingOf(value?.pending);
  await storage.setJson(ATTEND_KEY, {
    selectedSale: value?.selectedSale ?? null,
    recentSales: (value?.recentSales ?? []).slice(0, RECENTS_MAX),
    pending: stillPending
  });
  if (nothingPending(stillPending)) {
    await storage.remove(LEGACY_ATTEND_KEY);
  }
}

export async function clearAttendance() {
  const storage = useDeviceStorage();
  await storage.remove(ATTEND_KEY);
  await storage.remove(LEGACY_ATTEND_KEY);
}

export const ATTEND_STORAGE_KEY = ATTEND_KEY;
export const LEGACY_ATTEND_STORAGE_KEY = LEGACY_ATTEND_KEY;
export const saleSelectionStorage = { getAttendance, setAttendance, clearAttendance, RECENTS_MAX };
export default saleSelectionStorage;
